import { Controller } from "@hotwired/stimulus"

// BD reader modal — full port of the canonical mockup
// (selection-bd-monthly-v1 (1).html, lines ~2835–3084).
//
// Owns: spread building (desktop pairs / mobile single), navigation
// (arrows, tap zones, dots, keyboard), zoom (double-click / double-tap
// + dedicated zoom button), pan (mouse drag + single-finger touch),
// pinch-to-zoom (two fingers), swipe nav (one finger when not zoomed),
// and the "Je la choisis" CTA on the last spread.
//
// State held in Stimulus Values where possible (open/spreadIndex/zoomed)
// so HTML reflects the source of truth and `*ValueChanged` callbacks
// drive rendering. Transient gesture state (drag deltas, touch
// distances, last-tap timestamp) lives on `this` because it's
// per-gesture and never read from the DOM.
//
// Pan + touch listeners are wired via `data-action` descriptors on the
// modal root with @document scope where needed — Stimulus owns the
// listener lifecycle, no manual addEventListener / removeEventListener.
//
// Webpacker/Babel in this repo does NOT transform optional chaining
// (`?.`); use explicit `obj && obj.foo` checks throughout.

const ZOOM_LEVEL = 2.2          // desktop double-click zoom factor (mockup parity)
const ZOOM_LEVEL_MOBILE = 2.5   // mobile double-tap zoom factor (mockup parity)
const HINT_DURATION_MS = 4000   // hint pill auto-hide (mockup parity)
const FADE_MS = 150             // book fade duration during page swap
const TAP_DELAY_MS = 230        // delay so a double-click is not lost as nav
const DOUBLE_TAP_MS = 300       // touch double-tap window
const SWIPE_THRESHOLD_PX = 50   // touch swipe nav threshold
const MOBILE_BREAKPOINT_PX = 680

export default class extends Controller {
  static targets = [
    "overlay", "title", "author",
    "stage", "book", "prevBtn", "nextBtn", "dots", "cta",
    "zoomBtn", "hint"
  ]

  static values = {
    spreadIndex: { type: Number, default: 0 },
    spreads: { type: Array, default: [] },
    zoomed: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    bdName: { type: String, default: "" }
  }

  static outlets = ["bd-selection"]

  // Runs before any `*ValueChanged` callback fires on boot, so transient
  // state (mZoom, timers, drag origin) is safe to read from those callbacks
  // even on the very first render.
  initialize() {
    this.fadeTimer = null
    this.hintTimer = null
    this.tapTimer = null
    this.resizeTimer = null
    this.peekStartTimer = null
    this.peekEndTimer = null
    this.pages = []
    this.mZoom = {
      s: 1, tx: 0, ty: 0,
      startDist: 0, startScale: 1,
      startTx: 0, startTy: 0,
      panStart: null, lastTap: 0
    }
    this.dragging = false
    this.dragOrigin = null
    this.touchStartX = 0
  }

  connect() {
    // Touch swipe-nav transient state — `rTx`/`rTd` from the mockup.
    this.touchStartX = 0
    this.touchDelta = 0
  }

  disconnect() {
    this.clearTimers()
    this.unlockBody()
  }

  // ── Public API (called by bd-selection via outlet) ─────────────────────────
  // Signature: open(pagesJson, meta) where
  //   pagesJson = JSON-stringified array of image URLs (or already an array)
  //   meta      = { title, author } — title doubles as the lookup key
  //               for the "Je la choisis" callback.
  open(pagesJson, meta) {
    const pages = this.parsePages(pagesJson)
    if (pages.length === 0) return

    const safeMeta = meta || {}
    this.pages = pages
    this.bdNameValue = safeMeta.title || ""

    if (this.hasTitleTarget) this.titleTarget.textContent = safeMeta.title || ""
    if (this.hasAuthorTarget) this.authorTarget.textContent = safeMeta.author || ""

    this.preload(pages)
    this.spreadsValue = this.buildSpreads(pages)
    this.spreadIndexValue = 0
    this.openValue = true
    this.maybeShowHint()
    this.peekHint()
  }

  close() {
    // Esc-while-zoomed first dezooms (mockup parity).
    if (this.zoomedValue) {
      this.zoomedValue = false
      return
    }
    this.openValue = false
  }

  // Called by the "Je la choisis" CTA on the last spread. Closes the
  // reader and asks the bd-selection outlet to mark the BD as the
  // monthly pick. Falls back to clicking `.bd-cta-main` on the source
  // card directly if the outlet isn't wired (defensive — should not
  // happen in production).
  choose() {
    const name = this.bdNameValue
    this.openValue = false
    if (this.hasBdSelectionOutlet && this.bdSelectionOutlet.chooseFromReader) {
      this.bdSelectionOutlet.chooseFromReader(name)
      return
    }
    const escaped = (window.CSS && window.CSS.escape) ? window.CSS.escape(name) : name
    const card = document.querySelector('[data-bd-title="' + escaped + '"]')
    if (card) {
      const btn = card.querySelector(".bd-cta-main")
      if (btn) btn.click()
    }
  }

  // ── Navigation ────────────────────────────────────────────────────────────
  next()  { this.go(1) }
  prev()  { this.go(-1) }

  go(direction) {
    if (this.mZoom.s > 1) return // mockup: no nav while zoomed
    const next = this.spreadIndexValue + direction
    if (next >= 0 && next < this.spreadsValue.length) {
      this.spreadIndexValue = next
    }
  }

  // Tap zones — schedule with a small delay so a quick double-click on
  // the stage is interpreted as zoom rather than two navigations.
  tapPrev() { this.scheduleTap(-1) }
  tapNext() { this.scheduleTap(1) }

  scheduleTap(direction) {
    if (this.isMobile() || this.mZoom.s > 1) return
    clearTimeout(this.tapTimer)
    this.tapTimer = setTimeout(() => this.go(direction), TAP_DELAY_MS)
  }

  // Dot click handler — wired via inline data-action on each dot
  // (controller renders dots with `data-action="click->bd-reader#goTo"`).
  goTo(event) {
    if (this.mZoom.s > 1) return
    const target = event.currentTarget
    const index = Number(target.dataset.spreadIndex)
    if (Number.isInteger(index)) this.spreadIndexValue = index
  }

  // ── Zoom ──────────────────────────────────────────────────────────────────
  toggleZoom() {
    if (this.mZoom.s > 1) {
      this.zoomReset(true)
    } else if (this.hasStageTarget) {
      const stage = this.stageTarget.getBoundingClientRect()
      this.zoomAtPoint(stage.left + stage.width / 2, stage.top + stage.height / 2, ZOOM_LEVEL)
    }
  }

  // Desktop only — double-click anywhere on the stage zooms to the
  // click point (or dezooms if already zoomed). Mockup parity.
  stageDblClick(event) {
    if (this.isMobile()) return
    clearTimeout(this.tapTimer) // cancel any pending tap-nav
    if (this.mZoom.s > 1) {
      this.zoomReset(true)
    } else {
      this.zoomAtPoint(event.clientX, event.clientY, ZOOM_LEVEL)
    }
    if (event.preventDefault) event.preventDefault()
  }

  zoomAtPoint(clientX, clientY, targetScale) {
    if (!this.hasStageTarget) return
    const stage = this.stageTarget.getBoundingClientRect()
    const cx = clientX - stage.left - stage.width / 2
    const cy = clientY - stage.top - stage.height / 2
    this.mZoom.s = targetScale
    this.mZoom.tx = -cx * (targetScale - 1)
    this.mZoom.ty = -cy * (targetScale - 1)
    this.clampPan()
    this.applyZoom(true)
  }

  zoomReset(animated) {
    this.mZoom.s = 1
    this.mZoom.tx = 0
    this.mZoom.ty = 0
    this.applyZoom(animated)
  }

  applyZoom(animated) {
    if (!this.hasBookTarget) return
    const book = this.bookTarget
    book.style.transition = animated ? "transform 0.28s cubic-bezier(0.4,0,0.2,1)" : "none"
    book.style.transform = "translate(" + this.mZoom.tx + "px," + this.mZoom.ty + "px) scale(" + this.mZoom.s + ")"
    // Drive the `is-zoomed` class via the Value so reactive callbacks
    // can pick it up (cursor swap, hint hide, etc.).
    this.zoomedValue = this.mZoom.s > 1
  }

  clampPan() {
    if (!this.hasBookTarget || !this.hasStageTarget) return
    const bookRect = this.bookTarget.getBoundingClientRect()
    const stage = this.stageTarget.getBoundingClientRect()
    const maxX = Math.max(0, (bookRect.width - stage.width) / 2)
    const maxY = Math.max(0, (bookRect.height - stage.height) / 2)
    this.mZoom.tx = Math.max(-maxX, Math.min(maxX, this.mZoom.tx))
    this.mZoom.ty = Math.max(-maxY, Math.min(maxY, this.mZoom.ty))
  }

  // ── Desktop drag-pan ──────────────────────────────────────────────────────
  // Mousedown is on the book element; mousemove/mouseup are wired on
  // @document so the gesture continues even if the cursor leaves the
  // book mid-drag.
  dragStart(event) {
    if (this.mZoom.s <= 1 || this.isMobile()) return
    this.dragging = true
    this.dragOrigin = {
      x: event.clientX,
      y: event.clientY,
      tx: this.mZoom.tx,
      ty: this.mZoom.ty
    }
    if (this.hasBookTarget) this.bookTarget.classList.add("is-panning")
    if (event.preventDefault) event.preventDefault()
  }

  dragMove(event) {
    if (!this.dragging || !this.dragOrigin) return
    this.mZoom.tx = this.dragOrigin.tx + (event.clientX - this.dragOrigin.x)
    this.mZoom.ty = this.dragOrigin.ty + (event.clientY - this.dragOrigin.y)
    this.clampPan()
    this.applyZoom(false)
  }

  dragEnd() {
    if (!this.dragging) return
    this.dragging = false
    this.dragOrigin = null
    if (this.hasBookTarget) this.bookTarget.classList.remove("is-panning")
  }

  // ── Touch: pinch / pan / double-tap / swipe ───────────────────────────────
  touchStart(event) {
    if (event.touches.length === 2) {
      this.mZoom.startDist = this.touchDist(event.touches[0], event.touches[1])
      this.mZoom.startScale = this.mZoom.s
      this.touchDelta = 0
      this.touchStartX = 0
    } else if (event.touches.length === 1) {
      this.touchStartX = event.touches[0].clientX
      this.touchDelta = 0
      if (this.mZoom.s > 1) {
        this.mZoom.panStart = { x: event.touches[0].clientX, y: event.touches[0].clientY }
        this.mZoom.startTx = this.mZoom.tx
        this.mZoom.startTy = this.mZoom.ty
      }
    }
  }

  touchMove(event) {
    if (event.touches.length === 2) {
      if (this.mZoom.startDist > 0) {
        const d = this.touchDist(event.touches[0], event.touches[1])
        const next = this.mZoom.startScale * d / this.mZoom.startDist
        this.mZoom.s = Math.min(4, Math.max(1, next))
        if (this.mZoom.s <= 1) {
          this.mZoom.tx = 0
          this.mZoom.ty = 0
        } else {
          this.clampPan()
        }
        this.applyZoom(false)
        if (event.cancelable && event.preventDefault) event.preventDefault()
      }
    } else if (event.touches.length === 1) {
      if (this.mZoom.s > 1 && this.mZoom.panStart) {
        this.mZoom.tx = this.mZoom.startTx + (event.touches[0].clientX - this.mZoom.panStart.x)
        this.mZoom.ty = this.mZoom.startTy + (event.touches[0].clientY - this.mZoom.panStart.y)
        this.clampPan()
        this.applyZoom(false)
        if (event.cancelable && event.preventDefault) event.preventDefault()
      } else {
        // Track horizontal delta for swipe nav.
        this.touchDelta = event.touches[0].clientX - this.touchStartX
      }
    }
  }

  touchEnd(event) {
    // Double-tap → zoom in/out (only when no pinch was in progress and
    // movement under threshold).
    if (this.isMobile() && Math.abs(this.touchDelta) < 10
        && event.changedTouches.length === 1 && this.mZoom.startDist === 0) {
      const now = Date.now()
      if (now - this.mZoom.lastTap < DOUBLE_TAP_MS) {
        const t = event.changedTouches[0]
        if (this.mZoom.s > 1) {
          this.zoomReset(true)
        } else {
          this.zoomAtPoint(t.clientX, t.clientY, ZOOM_LEVEL_MOBILE)
        }
        this.mZoom.lastTap = 0
        if (event.cancelable && event.preventDefault) event.preventDefault()
        return
      }
      this.mZoom.lastTap = now
    }
    this.mZoom.startDist = 0
    this.mZoom.panStart = null
    // Swipe nav only when not zoomed.
    if (this.mZoom.s === 1 && Math.abs(this.touchDelta) > SWIPE_THRESHOLD_PX) {
      if (this.touchDelta < 0) this.go(1)
      else this.go(-1)
    }
    this.touchDelta = 0
  }

  // ── Keyboard ──────────────────────────────────────────────────────────────
  handleKey(event) {
    if (!this.openValue) return
    if (event.key === "ArrowRight") this.go(1)
    else if (event.key === "ArrowLeft") this.go(-1)
    else if (event.key === "Escape") {
      if (this.mZoom.s > 1) this.zoomReset(true)
      else this.openValue = false
    }
  }

  // ── Resize handling ───────────────────────────────────────────────────────
  // Rebuild spreads on viewport changes (debounced) so a desktop user
  // dragging across the mobile breakpoint sees the right layout.
  handleResize() {
    if (!this.openValue || this.pages.length === 0) return
    clearTimeout(this.resizeTimer)
    this.resizeTimer = setTimeout(() => {
      const next = this.buildSpreads(this.pages)
      if (next.length !== this.spreadsValue.length) {
        const currentFirstPage = (this.spreadsValue[this.spreadIndexValue] || [])[0]
        this.spreadsValue = next
        const newIndex = next.findIndex((s) => s[0] === currentFirstPage)
        this.spreadIndexValue = newIndex >= 0 ? newIndex : 0
      }
    }, 150)
  }

  // ── Value-changed callbacks drive all rendering ──────────────────────────
  openValueChanged() {
    if (!this.hasOverlayTarget) return
    if (this.openValue) {
      // `[hidden]` carries the visibility (display:none) — clearing it
      // restores the modal's flex column layout.
      this.overlayTarget.hidden = false
      this.lockBody()
      this.renderDots()
      this.renderSpread()
    } else {
      this.overlayTarget.hidden = true
      this.unlockBody()
      this.zoomReset(false)
      this.hideHint()
      if (this.hasZoomBtnTarget) this.zoomBtnTarget.classList.remove("is-active")
    }
  }

  spreadIndexValueChanged() {
    if (!this.openValue) return
    this.renderSpread()
    this.renderDotsActive()
  }

  spreadsValueChanged() {
    if (!this.openValue) return
    this.renderDots()
  }

  zoomedValueChanged() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.toggle("is-zoomed", this.zoomedValue)
    if (this.hasZoomBtnTarget) {
      this.zoomBtnTarget.classList.toggle("is-active", this.zoomedValue)
    }
    if (this.zoomedValue) this.hideHint()
  }

  // ── Rendering ────────────────────────────────────────────────────────────
  renderSpread() {
    const spread = this.spreadsValue[this.spreadIndexValue]
    if (!spread || !this.hasBookTarget) return

    // Reset zoom/pan before swapping pages (mockup: mZoomReset(false)).
    this.zoomReset(false)

    const book = this.bookTarget
    book.classList.add("fading")
    clearTimeout(this.fadeTimer)
    this.fadeTimer = setTimeout(() => {
      this.applySpread(spread)
      book.classList.remove("fading")
    }, FADE_MS)

    this.updateArrowState()
    this.updateCtaState()
  }

  applySpread(spread) {
    if (!this.hasBookTarget) return
    const book = this.bookTarget
    book.innerHTML = ""
    book.classList.toggle("single", spread.length === 1)
    spread.forEach((src, i) => {
      const wrap = document.createElement("div")
      if (spread.length === 2) {
        wrap.className = "bd-reader-page " + (i === 0 ? "bd-reader-page-left" : "bd-reader-page-right")
      } else {
        wrap.className = "bd-reader-page"
      }
      const img = document.createElement("img")
      img.src = src
      img.alt = ""
      wrap.appendChild(img)
      book.appendChild(wrap)
    })
  }

  updateArrowState() {
    const lastIndex = this.spreadsValue.length - 1
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.classList.toggle("is-disabled", this.spreadIndexValue === 0)
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.classList.toggle("is-disabled", this.spreadIndexValue === lastIndex)
    }
  }

  updateCtaState() {
    if (!this.hasCtaTarget) return
    const isLast = this.spreadIndexValue === this.spreadsValue.length - 1
    this.ctaTarget.classList.toggle("show", isLast)
  }

  renderDots() {
    if (!this.hasDotsTarget) return
    this.dotsTarget.innerHTML = ""
    const count = this.spreadsValue.length
    for (let i = 0; i < count; i += 1) {
      const dot = document.createElement("button")
      dot.type = "button"
      dot.className = "bd-reader-dot"
      if (i === this.spreadIndexValue) dot.classList.add("is-on")
      dot.dataset.spreadIndex = i
      dot.dataset.action = "click->bd-reader#goTo"
      dot.setAttribute("aria-label", "Aller à la planche " + (i + 1))
      this.dotsTarget.appendChild(dot)
    }
  }

  renderDotsActive() {
    if (!this.hasDotsTarget) return
    const dots = this.dotsTarget.querySelectorAll(".bd-reader-dot")
    dots.forEach((dot, i) => dot.classList.toggle("is-on", i === this.spreadIndexValue))
  }

  // ── Helpers ──────────────────────────────────────────────────────────────
  parsePages(input) {
    if (Array.isArray(input)) return input.filter(Boolean)
    if (typeof input !== "string" || input.length === 0) return []
    try {
      const parsed = JSON.parse(input)
      return Array.isArray(parsed) ? parsed.filter(Boolean) : []
    } catch (_err) {
      return []
    }
  }

  // Mockup `buildSpreads`: desktop pairs from page 0 (no special cover),
  // mobile single page each.
  buildSpreads(pages) {
    if (this.isMobile()) return pages.map((p) => [p])
    const spreads = []
    for (let i = 0; i < pages.length; i += 2) {
      if (i + 1 < pages.length) spreads.push([pages[i], pages[i + 1]])
      else spreads.push([pages[i]])
    }
    return spreads
  }

  isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT_PX
  }

  isCoarsePointer() {
    return Boolean(window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
  }

  preload(pages) {
    pages.forEach((src) => {
      if (!src) return
      const img = new Image()
      img.src = src
    })
  }

  touchDist(a, b) {
    const dx = a.clientX - b.clientX
    const dy = a.clientY - b.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  maybeShowHint() {
    if (!this.hasHintTarget) return
    // Device-aware copy. `isMobile()` mirrors the mockup; the coarse-
    // pointer media query is kept as a fallback for narrow desktops
    // with touch support.
    this.hintTarget.textContent = (this.isMobile() || this.isCoarsePointer())
      ? "Double-tapez ou pincez pour zoomer"
      : "Double-cliquez pour zoomer"
    this.hintTarget.classList.add("show")
    clearTimeout(this.hintTimer)
    this.hintTimer = setTimeout(() => this.hideHint(), HINT_DURATION_MS)
  }

  hideHint() {
    if (!this.hasHintTarget) return
    clearTimeout(this.hintTimer)
    this.hintTarget.classList.remove("show")
  }

  // Mobile-only "peek" affordance: when the viewer opens, the spread
  // slides ~28px to the left and snaps back so users see the swipe
  // gesture is available. CSS owns the keyframe; we just toggle the
  // class on then off. Skipped on desktop and on single-spread BDs.
  peekHint() {
    if (!this.hasBookTarget) return
    if (!this.isMobile()) return
    if (!this.spreadsValue || this.spreadsValue.length <= 1) return

    const book = this.bookTarget
    clearTimeout(this.peekStartTimer)
    clearTimeout(this.peekEndTimer)
    this.peekStartTimer = setTimeout(() => {
      book.classList.add("peek-hint")
      this.peekEndTimer = setTimeout(() => book.classList.remove("peek-hint"), 1050)
    }, 550)
  }

  lockBody() {
    document.body.style.overflow = "hidden"
  }

  unlockBody() {
    document.body.style.overflow = ""
  }

  clearTimers() {
    clearTimeout(this.fadeTimer)
    clearTimeout(this.hintTimer)
    clearTimeout(this.tapTimer)
    clearTimeout(this.resizeTimer)
    clearTimeout(this.peekStartTimer)
    clearTimeout(this.peekEndTimer)
  }
}
